/* The Spring Drop landing (sections/spring-drop-landing.liquid): behaviour only. Ported from the approved prototype's
   app.js (_companion/hha-spring-sale-claude-2026-09-23). Markup and product data are rendered by Liquid: #sp-data holds
   the live variant ids, titles and availability. Every add is a real /cart/add.js call, then the Opus cart drawer
   opens (or quietly refreshes) exactly as snippets/hh-cart-ui-bridge.liquid would do it (see notifyCart). Not ported from the
   prototype: the board toggles (hero options, header modes, offer state, motion override), the replica header,
   drawer and footer, and the new-tab links. */
(() => {
  'use strict';
  // The page's own fetch, captured before the theme's sitewide assets/hha-buypath-fallback.js wraps it (that script is
  // deferred in the footer, so it always runs after this one). See "Why this page talks to Opus directly" below.
  const pageFetch = window.fetch.bind(window);
  const root = document.querySelector('.sp-root');
  if (!root) return;
  const page = root.querySelector('.sp-page');
  const dataEl = root.querySelector('#sp-data');
  if (!page || !dataEl) return;
  const DATA = JSON.parse(dataEl.textContent);
  const byHandle = Object.fromEntries(DATA.products.map(p => [p.handle, p]));

  // motion values live in the section's tokens (scoped to .sp-root); read them once
  const css = getComputedStyle(root);
  const ms = name => { const v = css.getPropertyValue(name).trim(); return v.endsWith('ms') ? parseFloat(v) : parseFloat(v) * 1000; };
  const M = { hold: ms('--m-added-hold') || 2400 };
  const doc = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () => reduce.matches;
  // the live theme's header section (sections/header.liquid): sticky, announcement + ink bar, rendered outside the page
  const hdrSec = document.getElementById('shopify-section-header');
  const hdrBar = hdrSec ? hdrSec.querySelector('.hh-hdr') : null;

  /* ---------------- buying: one popover/sheet flow, shared by the tile "+", the story "+" and the feature/promo
     "Add to cart" pill (round 9). One-size pieces add in a single tap, the rest open the size picker (bottom sheet
     under 560px, an inline popover on the host from 560 up). ---------------- */
  const cartDot = hdrSec ? hdrSec.querySelector('.site-header__cart-indicator') : null;
  const addStatus = page.querySelector('[data-add-status]');
  const say = (btn, text) => { const m = btn.querySelector('.sp-add-m'); if (m) m.textContent = text === 'Add to cart' ? '' : text; };
  const pick = document.getElementById('sp-pick');
  const pickScrim = root.querySelector('.sp-pick-scrim');
  const pickSizes = pick.querySelector('.sp-sizes');
  const pickMsg = pick.querySelector('.sp-pick-msg');
  const setMsg = (text, tone) => { pickMsg.textContent = text; if (tone) pickMsg.dataset.tone = tone; else delete pickMsg.dataset.tone; };
  let pickHost = null, pickBtn = null;

  /* The add sequence is airtight. Live bugs, 24 Sep: the Commander ("clicking the size add to cart sometimes sends me
     to the natty shopify cart"), then an independent review and a re-review (REVIEW-INDEPENDENT.md in the go-live job).
     F1, R2, R3. On phones the size sheet opens at the bottom of the screen the instant "+" is tapped, so the second tap
         of a double tap on a low tile landed on a size and added a size nobody chose. For a touch or pen press, a tap on
         the picker (a size, its close X, its scrim, or outside it) only counts when the press began on screen after the
         sheet had painted (two animation frames after it opened), and, if the press is within REPEAT_PX of the press
         that opened it, only once REPEAT_MS have passed since the paint: that is a re-tap of the same spot, not an aim
         at a size. A press anywhere else counts straight away. Mouse and keyboard are never held back (a double click
         on desktop lands on the popover's empty message line, so the guard would protect nothing there).
     F2, R1. From the tap that adds (a size, or a one-size "+") until the Opus drawer has fully landed, a shield
         swallows every real tap anywhere on the page, before Opus or any link sees it: the picker, the tiles, the
         drawer, its overlay and its close control. While the drawer is still on its way, each swallowed tap extends the
         shield (nervous tapping stays harmless); from the moment it lands the shield ends on a fixed GHOST_MS timer that
         nothing extends, so the first tap on the drawer's close or Checkout after that works. It never lasts longer than
         SHIELD_MAX_MS. Taps are swallowed at pointer, mouse and click level; the touch listeners stay passive (R4), so
         scrolling starts at once even while the shield is up. The picker stays up (inert) until the drawer has landed,
         and a drawer that vanishes before then, or during the GHOST_MS after (unless the shopper pressed Escape), is asked
         to open again at once. With no drawer owed (Opus absent, or its "open cart drawer after adding" setting off) the
         picker holds its "Added" line for NO_DRAWER_HOLD_MS and then everything is released. */
  const GHOST_MS = 1200, SHIELD_MAX_MS = 10000, NO_DRAWER_HOLD_MS = 2500, REPEAT_PX = 40, REPEAT_MS = 800;
  const now = () => Date.now();
  const drawerEl = () => document.getElementById('cart-drawer-container');
  // Opus's panel slides in from the side (a transform), and its see-through overlay can be laid down a moment before
  // the panel moves, so the panel's own box is the test; a plain hit test on the right edge is the fallback if Opus
  // ever renames it.
  const drawerShowing = () => {
    const c = drawerEl();
    if (!c) return false;
    const panel = c.querySelector('[class*="drawer-cd-up-app"]');
    if (panel) {
      const r = panel.getBoundingClientRect();
      return r.width > 100 && r.left < innerWidth - 60 && r.right > 60 && getComputedStyle(panel).visibility !== 'hidden';
    }
    const hit = document.elementFromPoint(innerWidth - 12, Math.round(innerHeight / 2));
    return !!hit && c.contains(hit) && parseFloat(getComputedStyle(hit).opacity) > 0.5;
  };
  // landed: the panel has stopped moving with all of it on screen (no assumption about which side it comes from)
  let lastLeft = null;
  const drawerSettled = () => {
    const panel = drawerEl() && drawerEl().querySelector('[class*="drawer-cd-up-app"]');
    if (!panel) return drawerShowing();
    const r = panel.getBoundingClientRect();
    const still = lastLeft !== null && Math.abs(r.left - lastLeft) < 0.5;
    lastLeft = r.left;
    return r.width > 100 && r.left >= -1.5 && r.right <= innerWidth + 1.5 && still;
  };
  // opusOpen does nothing when the drawer is already open, so asking again is always safe
  const reopenDrawer = () => { try { if (typeof window.opusOpen === 'function') window.opusOpen(); } catch (e) { /* the retry chain in notifyCart carries on */ } };

  // the press that led to the latest click: when, where, with what (pointer events: every current browser has them)
  let lastDown = { t: 0, x: 0, y: 0, type: 'mouse', target: null };
  const onDown = e => { lastDown = { t: now(), x: e.clientX, y: e.clientY, type: e.pointerType || (e.type === 'touchstart' ? 'touch' : 'mouse'), target: e.target }; };
  let shieldStart = 0, shieldUntil = 0, drawerSeenAt = 0, landed = false, escapedAt = 0;
  const shieldUp = () => now() < shieldUntil;
  function raiseShield() { shieldStart = now(); shieldUntil = shieldStart + SHIELD_MAX_MS; landed = false; }
  function lowerShieldIn(ms) { shieldUntil = Math.min(now() + ms, shieldStart + SHIELD_MAX_MS); }
  const shieldTap = e => {
    if (!e.isTrusted) return; // Opus opens its drawer with a programmatic click on its own button: never block that
    if (!shieldUp()) { if (e.type === 'pointerdown' || (!('PointerEvent' in window) && (e.type === 'touchstart' || e.type === 'mousedown'))) onDown(e); return; }
    e.stopImmediatePropagation();
    if (e.cancelable && e.type !== 'touchstart' && e.type !== 'touchend') e.preventDefault();
    // before the drawer has landed a tap extends the shield; after that it never does (R1)
    if (!landed && (e.type === 'pointerdown' || e.type === 'mousedown')) shieldUntil = Math.max(shieldUntil, Math.min(now() + GHOST_MS, shieldStart + SHIELD_MAX_MS));
  };
  for (const type of ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click', 'dblclick', 'auxclick', 'contextmenu']) {
    addEventListener(type, shieldTap, { capture: true, passive: false });
  }
  // touch listeners are passive (R4): they never cancel, so the browser never waits on them to scroll
  for (const type of ['touchstart', 'touchend']) addEventListener(type, shieldTap, { capture: true, passive: true });
  addEventListener('keydown', e => { if (e.key === 'Escape') escapedAt = now(); }, true);

  // after a successful add: watch the drawer until it has landed (onDone(true)) or is not coming (onDone(false))
  function watchDrawer(addAt, onDone) {
    lastLeft = null;
    let firstSeen = 0, done = false;
    const finish = shown => { if (!done) { done = true; onDone(shown); } };
    const tick = () => {
      const showing = drawerShowing();
      if (showing && !firstSeen) { firstSeen = now(); drawerSeenAt = firstSeen; }
      // landed: at rest fully on screen, or on screen for 2.5s whatever it measures (a pinch-zoomed phone may never
      // measure fully inside the viewport, and the shield must not keep renewing itself for that)
      if (showing && (drawerSettled() || now() - firstSeen > 2500)) { finish(true); return; }
      // it showed and went away again before landing (not the shopper: the shield has every tap): ask again at once
      if (!showing && firstSeen) { firstSeen = 0; reopenDrawer(); }
      const since = now() - addAt;
      if ((!drawerOwed(since) && since > NO_DRAWER_HOLD_MS) || since > SHIELD_MAX_MS) { finish(false); return; }
      setTimeout(tick, 50);
    };
    tick();
  }
  // during the GHOST_MS after landing, a drawer that disappears is put back (Opus can re-mount on a slow load), unless
  // the shopper closed it with Escape (R5): taps cannot reach it in that window, so a vanishing drawer is not theirs
  function keepDrawerOpen(since) {
    if (!shieldUp() || escapedAt >= since) return;
    if (!drawerShowing()) reopenDrawer();
    setTimeout(() => keepDrawerOpen(since), 100);
  }
  // the whole tail of an add: a fixed GHOST_MS after the drawer lands, at once when no drawer is coming
  function afterAdd(addAt, onEnd) {
    watchDrawer(addAt, shown => {
      landed = true;
      if (shown) { lowerShieldIn(GHOST_MS); keepDrawerOpen(now()); } else lowerShieldIn(0);
      onEnd(shown);
    });
  }
  // does a click on the picker, its scrim or its trigger count? (F1, R2, R3: see the header of this block)
  let pickLocked = false, pickOpenedAt = 0, pickPaintedAt = Infinity, openDown = null;
  const armed = (e, zone) => {
    if (!e || e.detail === 0) return true;            // keyboard, assistive tech
    const d = lastDown;
    if (d.type !== 'touch' && d.type !== 'pen') return true; // mouse is never held back
    if (d.t < pickPaintedAt) return false;             // pressed before the sheet could be seen
    if (zone && !(d.target && zone.contains(d.target))) return false; // the press must start on the thing it activates
    const near = openDown && Math.hypot(d.x - openDown.x, d.y - openDown.y) < REPEAT_PX;
    return !near || d.t >= pickPaintedAt + REPEAT_MS;   // a re-tap of the opening spot waits; anywhere else counts now
  };

  /* Why this page talks to Opus directly (live bug, 24 Sep). The theme's sitewide assets/hha-buypath-fallback.js
     watches every successful /cart/add (it wraps fetch and XHR) and every 'cart:refresh' event; if the Opus drawer is
     not on screen 2.5s later it sends the shopper to /cart. On a slow phone, or while Opus is still loading, the drawer
     takes longer than that, so a size tap here sometimes ended on the native cart page (reproduced: Fast 3G + 4x CPU,
     add at 8.5s, drawer still loading, fallback navigates to /cart at 12.4s). This page never navigates on an add:
       - the add goes through pageFetch (above), so the fallback does not see it;
       - instead of dispatching 'cart:refresh' (which the fallback also watches), it does exactly what
         snippets/hh-cart-ui-bridge.liquid does on that event: open the drawer when Opus's "open cart drawer after
         adding" setting is on, otherwise refresh it after 500ms. It waits for Opus to finish loading first (up to
         OPUS_WAIT_MS), so an add tapped during page load still ends with the drawer, not with nothing;
       - if Opus never loads, the add has still happened: the tile or pill shows Added and the header cart dot lights.
     The fallback's other job, rescuing a drawer Checkout click that goes nowhere, is untouched. */
  const OPUS_WAIT_MS = 8000, OPUS_RETRY_MS = 3000, OPUS_RETRIES = 18;
  const opusSettings = () => { const w = window.opusWidget; return w && w.settings && w.settings.settings; };
  // is a drawer on its way? yes while Opus is still loading (up to OPUS_WAIT_MS), then only if its setting says so
  const drawerOwed = sinceAdd => {
    const st = opusSettings();
    if (st) return !!(st.customization && st.customization.openCartDrawerAfterAddingProduct) && typeof window.opusOpen === 'function';
    return sinceAdd < OPUS_WAIT_MS;
  };
  function notifyCart() {
    const addAt = Date.now();
    const settings = () => { const w = window.opusWidget; return w && w.settings && w.settings.settings; };
    const ready = () => typeof window.opusOpen === 'function' && !!settings();
    const open = (tries = 0) => {
      let owed = false;
      try {
        const c = settings() && settings().customization;
        owed = !!(c && c.openCartDrawerAfterAddingProduct && typeof window.opusOpen === 'function');
        if (owed) window.opusOpen();
        else setTimeout(() => { try { if (typeof window.opusRefreshCart === 'function') window.opusRefreshCart(); } catch (e) { /* best effort */ } }, 500);
      } catch (e) { /* Opus threw mid-mount: the retry below tries again; the add itself already succeeded */ }
      // opusOpen works by clicking Opus's own hidden cart button, and a call made before Opus has finished mounting is
      // lost (traced at Fast 3G + 4x CPU: the first call at 10.1s did nothing, the next one 3s later opened it). So it
      // is asked again every OPUS_RETRY_MS for up to about a minute, and stops as soon as the panel has been seen once
      // (even if the shopper has already closed it again) or Opus marks it open, so a slow slide-in is never toggled shut.
      if (owed) setTimeout(() => {
        if (drawerSeenAt >= addAt || drawerShowing() || document.querySelector('#cart-drawer-container .open[class*="drawer-cd-up-app"]')) return;
        if (tries < OPUS_RETRIES) open(tries + 1);
      }, OPUS_RETRY_MS);
    };
    if (ready()) { open(); return; }
    const t0 = Date.now();
    const poll = () => {
      if (ready() || Date.now() - t0 > OPUS_WAIT_MS) open();
      else setTimeout(poll, 150);
    };
    poll();
  }

  // the real add. 422 = Shopify refused the variant (sold out, or no stock left beyond what is already in the cart).
  async function addToCart(v) {
    try {
      const r = await pageFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] }),
      });
      if (r.ok) { notifyCart(); return { ok: true }; }
      return { ok: false, status: r.status };
    } catch (e) {
      return { ok: false, status: 0 };
    }
  }
  function markAdded(host, btn, v) {
    const p = byHandle[host.dataset.product];
    btn.dataset.state = 'added'; btn.dataset.variant = v.id; say(btn, 'Added');
    if (cartDot) cartDot.classList.remove('hide');
    if (addStatus) addStatus.textContent = `Added ${p.title}${p.variants.length > 1 ? ', ' + v.title : ''} to your cart.`;
    clearTimeout(btn._t);
    btn._t = setTimeout(() => { btn.dataset.state = ''; say(btn, 'Add to cart'); }, M.hold);
  }
  function markSoldOut(v) { v.available = false; }
  // one-size pieces: the round "+" adds straight away; a failure shows on the button itself
  async function quickAdd(host, btn, v) {
    if (btn.getAttribute('aria-busy') === 'true') return;
    raiseShield();
    btn.setAttribute('aria-busy', 'true');
    const addAt = now();
    const res = await addToCart(v);
    btn.removeAttribute('aria-busy');
    if (res.ok) { markAdded(host, btn, v); afterAdd(addAt, () => {}); return; }
    lowerShieldIn(0);
    const p = byHandle[host.dataset.product];
    const label = btn.querySelector('.sp-plus-t');
    if (res.status === 422) { markSoldOut(v); btn.disabled = true; if (label) label.textContent = 'Sold out'; }
    else if (label) label.textContent = 'Try again';
    btn.dataset.state = 'error';
    if (addStatus) addStatus.textContent = res.status === 422 ? `${p.title} just sold out.` : `Could not add ${p.title}. Try again.`;
    clearTimeout(btn._t);
    btn._t = setTimeout(() => { btn.dataset.state = ''; if (label) label.textContent = 'Added'; }, M.hold);
  }
  function closePick(returnFocus) {
    if (!pickHost) return;
    pickLocked = false;
    pick.removeAttribute('aria-busy');
    const btn = pickBtn;
    pick.hidden = true; pickScrim.hidden = true;
    pickHost.classList.remove('is-picking');
    btn.setAttribute('aria-expanded', 'false');
    root.appendChild(pick);
    if (returnFocus) btn.focus({ preventScroll: true });
    pickHost = null; pickBtn = null;
  }
  function openPick(host, btn, e) {
    const p = byHandle[host.dataset.product];
    if (!p) return;
    if (p.variants.length === 1) { if (p.variants[0].available) quickAdd(host, btn, p.variants[0]); return; }
    // a second click on the same trigger closes the picker, but not the second half of the double tap that opened it
    if (pickHost === host) { if (armed(e) && !pickLocked) closePick(true); return; }
    closePick(false);
    pickOpenedAt = now(); pickPaintedAt = Infinity; openDown = e && e.detail !== 0 ? { ...lastDown } : null;
    const opened = pickOpenedAt;
    requestAnimationFrame(() => requestAnimationFrame(() => { if (pickOpenedAt === opened) pickPaintedAt = now(); }));
    pick.querySelector('.sp-pick-name').textContent = p.title;
    pick.querySelector('.sp-pick-price').innerHTML = host.querySelector('.sp-price').innerHTML;
    pickSizes.setAttribute('aria-label', p.title + ' sizes');
    pickSizes.innerHTML = '';
    for (const v of p.variants) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'sp-size'; b.dataset.variant = v.id; b.setAttribute('aria-pressed', 'false');
      b.textContent = v.title;
      if (!v.available) { b.disabled = true; b.setAttribute('aria-label', `${v.title}, sold out`); }
      pickSizes.appendChild(b);
    }
    setMsg('');
    const sheet = page.getBoundingClientRect().width < 560;
    pick.classList.toggle('is-sheet', sheet);
    pick.classList.toggle('is-pop', !sheet);
    if (sheet) { root.appendChild(pick); pickScrim.hidden = false; }
    else (host.querySelector('.sp-card-media, .sp-buy, [data-pick-anchor]') || host).appendChild(pick);
    pick.hidden = false;
    host.classList.add('is-picking');
    btn.setAttribute('aria-expanded', 'true');
    pickHost = host; pickBtn = btn;
    pickSizes.querySelector('.sp-size:not(:disabled)')?.focus({ preventScroll: true });
  }
  pick.addEventListener('click', async e => {
    // one add per opening, and nothing counts that was pressed before the picker could be seen (F1)
    if (pickLocked || !armed(e, pick)) { e.preventDefault(); e.stopPropagation(); return; }
    if (e.target.closest('[data-pick-close]')) { closePick(true); return; }
    const b = e.target.closest('.sp-size');
    if (!b || b.disabled || !pickHost) return;
    const host = pickHost, btn = pickBtn;
    const p = byHandle[host.dataset.product];
    const v = p.variants.find(x => String(x.id) === b.dataset.variant);
    if (!v || !v.available) return;
    pickLocked = true; pick.setAttribute('aria-busy', 'true');
    raiseShield();
    pickSizes.querySelectorAll('.sp-size').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    setMsg(`Adding, ${v.title}`);
    btn.setAttribute('aria-busy', 'true');
    const addAt = now();
    const res = await addToCart(v);
    btn.removeAttribute('aria-busy');
    if (res.ok) {
      if (pickHost === host) setMsg(`Added, ${v.title}`);
      markAdded(host, btn, v);
      // the drawer is the confirmation: the (inert) picker stays over the page until the drawer has landed, then leaves
      // at once; with no drawer coming it has held its "Added" line, then closes and hands focus back
      afterAdd(addAt, shown => { if (pickHost === host) closePick(!shown); });
      return;
    }
    lowerShieldIn(0);
    pickLocked = false; pick.removeAttribute('aria-busy');
    b.setAttribute('aria-pressed', 'false');
    if (res.status === 422) {
      markSoldOut(v);
      b.disabled = true; b.setAttribute('aria-label', `${v.title}, sold out`);
      if (pickHost === host) setMsg(`${v.title} just sold out.`, 'error');
    } else if (pickHost === host) setMsg('Could not add. Try again.', 'error');
  });
  pickScrim.addEventListener('click', e => { if (armed(e, pickScrim) && !pickLocked) closePick(true); });
  document.addEventListener('click', e => {
    if (pickHost && armed(e) && !pickLocked && !pick.contains(e.target) && !e.target.closest('[data-quick], [data-add]')) closePick(false);
  });
  addEventListener('keydown', e => { if (e.key === 'Escape' && pickHost) closePick(true); });
  // only a real width change closes the picker: iOS Safari fires resize as its toolbar shows and hides on scroll
  let lastW = innerWidth;
  addEventListener('resize', () => { if (innerWidth !== lastW) { lastW = innerWidth; if (!pickLocked) closePick(false); } });

  /* ---------------- tabs, sort and the count line ---------------- */
  const grid = page.querySelector('.sp-grid');
  const cards = [...page.querySelectorAll('.sp-card')];
  const story = page.querySelector('.sp-gridstory');
  const promo = page.querySelector('.sp-promo'); // round 8: the Fern and Fire promo, a full-width break after the sixth tile
  const countEl = page.querySelector('[data-count]');
  const sortEl = page.querySelector('[data-sort]');
  const view = { key: 'all', sort: 'featured' };
  const nowPrice = c => +c.querySelector('.sp-price').dataset.price;
  // round 11: the Green Horizon jacket has a second tile before the backpack (data-dup), there to even the full grid out
  // at two per row. It shows only in the full grid in its featured order; a tab or a price sort shows each piece once.
  const shown = c => (view.key === 'all' || c.dataset.cat === view.key) && !(c.dataset.dup && (view.key !== 'all' || view.sort !== 'featured'));
  function render() {
    closePick(false);
    page.querySelectorAll('.sp-filter').forEach(f => f.setAttribute('aria-pressed', String(f.dataset.filter === view.key)));
    const order = [...cards].sort((a, b) => {
      if (view.sort !== 'featured') { const d = nowPrice(a) - nowPrice(b); if (d) return view.sort === 'price-asc' ? d : -d; }
      return a.dataset.order - b.dataset.order;
    });
    order.forEach(c => grid.appendChild(c));
    cards.forEach(c => { c.hidden = !shown(c); c.classList.remove('is-lone-last'); });
    const vis = order.filter(c => !c.hidden);
    // the two full-width breaks keep their places whatever the sort: the promo after the sixth tile, the story after the twelfth
    if (promo && vis[6]) grid.insertBefore(promo, vis[6]);
    if (story && vis[12]) grid.insertBefore(story, vis[12]);
    if (story) story.hidden = view.key !== 'all';
    if (promo) promo.hidden = view.key !== 'all';
    // mobile 2-up grid: an odd visible count leaves the last tile alone in its row, centred by the CSS. Filtering hides
    // tiles with [hidden], so this class, not CSS position selectors, finds the true last visible tile.
    if (vis.length % 2 === 1) vis[vis.length - 1].classList.add('is-lone-last');
    // the count line counts pieces, so the repeated jacket counts once
    const n = new Set(vis.map(c => c.dataset.product)).size;
    countEl.textContent = `${n} piece${n === 1 ? '' : 's'} to make your own`;
  }
  const filter = key => { view.key = key; render(); };
  sortEl.addEventListener('change', () => { view.sort = sortEl.value; render(); });

  /* ---------------- one delegated click handler ---------------- */
  page.addEventListener('click', e => {
    const trigger = e.target.closest('[data-quick], [data-add]');
    if (trigger) { if (!trigger.disabled) openPick(trigger.closest('[data-product]'), trigger, e); return; }
    const f = e.target.closest('.sp-filter');
    if (f) { filter(f.dataset.filter); return; }
  });

  // round 11 (Commander: "remove the swipe left and right on the product images"): tiles show one photo, so the
  // swipe position dots and their scroll handler are gone

  /* ---------------- phone hero B1b (round 17, Commander 25 Sep: "yep, b1b is it now with these last changes. make it
     live"). Phones only (under 560 of page width) with motion: the photo runs up behind the see-through announcement
     bar and header, and as the page scrolls the header's ink ground (.hh-h7bg) fades in over the board's distance (2% to
     25% of 40% of a screen, about 7px to 84px on a 390x844 phone), then stays solid. Opacity only, no pin, no shrink.
     The inline script before the hero markup sets this state before the first paint; this keeps it up. Reduced motion,
     and 560px and wider, get the solid header (on phones the photo sits under it). */
  const hdr = (() => {
    const scrim = page.querySelector('.sp-b1-scrim');
    let bg = hdrSec ? hdrSec.querySelector(':scope > .hh-h7bg') : null;
    const clamp = v => Math.min(1, Math.max(0, v));
    const st = { on: false, D: 1, raf: 0 };
    const wanted = () => !!hdrSec && !reduced() && page.clientWidth < 560;
    function measure() { doc.style.setProperty('--h7-top', +hdrSec.getBoundingClientRect().height.toFixed(3) + 'px'); st.D = Math.max(1, Math.round(innerHeight * 0.4)); }
    function apply() {
      st.raf = 0; if (!st.on) return;
      const x = clamp((clamp(scrollY / st.D) - 0.02) / 0.23), b = x * x * (3 - 2 * x);
      bg.style.opacity = b.toFixed(3);
      if (scrim) scrim.style.opacity = (1 - b).toFixed(3);
      if ((b >= 1) !== doc.hasAttribute('data-h7done')) doc.toggleAttribute('data-h7done', b >= 1);
    }
    function enable() {
      if (!bg) { bg = document.createElement('div'); bg.className = 'hh-h7bg'; bg.setAttribute('aria-hidden', 'true'); hdrSec.prepend(bg); }
      st.on = true; doc.dataset.h7 = 'on'; measure(); apply();
    }
    function disable() {
      st.on = false; delete doc.dataset.h7; doc.removeAttribute('data-h7done');
      if (bg) bg.style.opacity = ''; if (scrim) scrim.style.opacity = '';
      doc.style.removeProperty('--h7-top');
    }
    function sync() { const w = wanted(); if (w && !st.on) enable(); else if (!w && st.on) disable(); else if (st.on) { measure(); apply(); } }
    addEventListener('scroll', () => { if (st.on && !st.raf) st.raf = requestAnimationFrame(apply); }, { passive: true });
    addEventListener('resize', () => { if (st.on || wanted()) sync(); });
    if (hdrSec && 'ResizeObserver' in window) new ResizeObserver(() => { if (st.on) sync(); }).observe(hdrSec);
    reduce.addEventListener?.('change', sync);
    // a phone that loads with reduced motion never had the pre-paint state; one that turns it on later drops it here
    sync();
    return { sync, state: () => ({ on: st.on, D: st.D }) };
  })();

  render(); // sets is-lone-last (and everything else render() owns) for the page's initial state

  /* ---------------- reveal on scroll ---------------- */
  const reveals = page.querySelectorAll('[data-reveal]');
  if (reduced() || !('IntersectionObserver' in window)) reveals.forEach(el => el.classList.add('in'));
  else {
    const io = new IntersectionObserver(es => es.forEach(en => { if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); } }), { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });
    reveals.forEach(el => io.observe(el));
  }

  /* ---------------- "Shop the drop": lands on the tab row (round 10, Commander: "i want the shop the drop button(s)
     to go to this frame"). #pieces is a real anchor, so the link works with no JS; this adds the live header bar's
     height as the offset (the announcement has scrolled away by then, so the bar is what covers the top) and smooth
     motion, skipped under reduced motion. ---------------- */
  const piecesEl = document.getElementById('pieces');
  root.querySelectorAll('a[href="#pieces"]').forEach(a => a.addEventListener('click', e => {
    if (!piecesEl) return;
    e.preventDefault();
    const offset = hdrBar ? Math.ceil(hdrBar.getBoundingClientRect().height) : 0;
    const top = piecesEl.getBoundingClientRect().top + scrollY - offset;
    scrollTo({ top, behavior: reduced() ? 'auto' : 'smooth' });
  }));

  // handle for the live verification script
  window.SpringDrop = { filter, render, view, hdr };
})();
